import { useCallback, useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';
import { contactDisplayName } from '../lib/contacts';
import HarryBox from '../ai/HarryBox';
import '../po/drawer.css';


function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).toLowerCase();
}

// Visual chip per update kind. Manual notes are blue (intentional);
// auto rows derive their colour from the underlying signal type so the
// eye can scan email/slack/transcript at a glance instead of every
// auto row looking identical.
function chipFor(update) {
  if (update.source === 'manual') {
    return { bg: '#EEF2FF', fg: '#3730A3', label: 'note' };
  }
  const st = update.signal_ref?.source_type;
  if (st === 'email')      return { bg: '#FEF3C7', fg: '#92400E', label: 'email' };
  if (st === 'slack' || st === 'chat') return { bg: '#E2F1E6', fg: '#0F5F2D', label: 'slack' };
  if (st === 'transcript') return { bg: '#EEF2FF', fg: '#3730A3', label: 'transcript' };
  return { bg: '#F4F4F5', fg: '#52525B', label: update.source || '?' };
}

// Strip the trigger-prefixed "[Email] " / "[Slack] " / "[Transcript] "
// tokens from the body when we're already showing a source chip — they
// were redundant + visually loud in the feed.
function cleanBody(body, source) {
  if (source !== 'auto' || !body) return body || '';
  return body.replace(/^\[(Email|Slack|Transcript|Signal)\]\s*/i, '');
}

// Dedupe auto-touchpoints that are obviously the same conversation:
// a single email thread with five replies on the same day is six rows
// in relationship_updates (one per source_id) but one event in human
// terms. Collapse by (source_type, normalised body, calendar day).
//
// Manual notes are NEVER deduped — every note is intentional.
function dedupeFeed(updates) {
  const seen = new Map();
  const out = [];
  for (const u of updates) {
    if (u.source !== 'auto') {
      out.push({ ...u, _dupes: 0 });
      continue;
    }
    const day = (u.created_at instanceof Date ? u.created_at : new Date(u.created_at))
      .toISOString().slice(0, 10);
    // Normalise: strip "Re:"/"Fwd:" prefixes and the [Email]/[Slack] tag,
    // lowercase, and drop trailing whitespace. Collapses replies that
    // only differ by reply-prefix.
    const norm = (u.body || '')
      .replace(/^\[(Email|Slack|Transcript|Signal)\]\s*/i, '')
      .replace(/^(Re|Fwd|FW)\s*:\s*/gi, '')
      .toLowerCase().trim();
    const key = `${u.signal_ref?.source_type || 'x'}|${norm}|${day}`;
    if (seen.has(key)) {
      const idx = seen.get(key);
      out[idx]._dupes += 1;
      continue;
    }
    seen.set(key, out.length);
    out.push({ ...u, _dupes: 0 });
  }
  return out;
}

export default function RelationshipDrawer({ session, relationship, onClose, onChanged }) {
  const c = relationship?.contact || {};
  const scopeLabel = contactDisplayName(c, 'person') || c.email || '(unknown)';

  // First-name token used to prefix presence_summary lines under each
  // auto-touchpoint ("Nathan was a recipient"). Falls back to the company
  // name, then the email local-part, then "They" so the line never reads
  // as a dangling phrase.
  const firstName = useMemo(() => {
    const person = (c.contact_person || '').trim().split(/\s+/)[0];
    if (person) return person;
    if ((c.company_name || '').trim()) return c.company_name.trim();
    if (c.email) return c.email.split('@')[0];
    return 'They';
  }, [c.contact_person, c.company_name, c.email]);

  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState(null);
  const [bumping, setBumping] = useState(false);
  // +Update compose state (Mo flagged 2026-05-10: button must open a
  // box for typing the note, not silently insert "Update").
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeText, setComposeText] = useState('');

  const isAdmin = session?.role === 'admin';
  // Mig 127: relationship comes from relationships_view which exposes
  // is_owner directly (derived from JWT). No PIN compare on the client.
  const isOwner = !!relationship?.is_owner;

  // Memoised, deduped feed. dedupeFeed collapses auto rows that share
  // (source_type, normalised body, day) so an email thread with five
  // replies on the same day becomes one row with a +4 badge.
  const dedupedUpdates = useMemo(() => dedupeFeed(updates), [updates]);

  const loadUpdates = useCallback(async () => {
    setLoadingUpdates(true);
    try {
      // Mig 127: read from the view (no author_pin in payload).
      // Mig 131: presence_summary explains how the contact was on the
      // signal — "Was a recipient", "Sent this email", etc.
      const { data } = await aws.from('relationship_updates_view')
        .select('id, body, source, signal_ref, author_name, is_author, presence_summary, team_participants, created_at, edited_at')
        .eq('relationship_id', relationship.id)
        .order('created_at', { ascending: false })
        .limit(100);
      setUpdates(data || []);
    } finally {
      setLoadingUpdates(false);
    }
  }, [relationship?.id]);

  useEffect(() => { loadUpdates(); }, [loadUpdates]);

  // ── +Update ──
  // Two-step: open compose box → type → Save inserts. Empty Save falls
  // back to body "Update" so users who just want a quick stamp still
  // can. The trg_bump_last_contact_at trigger bumps last_contact_at on
  // every insert so the tile sort + drawer header both refresh.
  const openCompose = useCallback(() => {
    setComposeOpen(true);
    setComposeText('');
    setError(null);
  }, []);

  const cancelCompose = useCallback(() => {
    setComposeOpen(false);
    setComposeText('');
  }, []);

  const saveCompose = useCallback(async () => {
    const trimmed = (composeText || '').trim();
    setBumping(true);
    setError(null);
    try {
      const { error } = await aws.from('relationship_updates').insert([{
        relationship_id: relationship.id,
        author_pin: session.code,
        body: trimmed || 'Update',
        source: 'manual',
      }]);
      if (error) throw error;
      setComposeOpen(false);
      setComposeText('');
      await loadUpdates();
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      setBumping(false);
    }
  }, [relationship?.id, session?.code, loadUpdates, onChanged, composeText]);

  // ── Edit / delete an update ──
  const startEdit = (u) => { setEditingId(u.id); setEditText(u.body || ''); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const saveEdit = useCallback(async () => {
    const body = (editText || '').trim();
    if (!body || !editingId) return;
    try {
      const { error } = await aws.from('relationship_updates')
        .update({ body, edited_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', editingId);
      if (error) throw error;
      cancelEdit();
      await loadUpdates();
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Edit failed');
    }
  }, [editingId, editText, loadUpdates, onChanged]);

  const deleteUpdate = useCallback(async (u) => {
    if (!confirm('Delete this update? This cannot be undone.')) return;
    try {
      const { error } = await aws.from('relationship_updates')
        .delete()
        .eq('id', u.id);
      if (error) throw error;
      await loadUpdates();
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  }, [loadUpdates, onChanged]);

  // ── Archive the relationship ──
  const archive = useCallback(async () => {
    if (!confirm('Archive this relationship? It disappears from the list. (Updates are kept.)')) return;
    try {
      const { error } = await aws.from('relationships')
        .update({ archived: true })
        .eq('id', relationship.id);
      if (error) throw error;
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e?.message || 'Archive failed');
    }
  }, [relationship?.id, onChanged, onClose]);

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer po-drawer" style={{ width: 520 }}>
        <div className="drawer-header">
          <div style={{ minWidth: 0 }}>
            <div className="drawer-title" style={{ marginBottom: 2 }}>{scopeLabel}</div>
            <div style={{ fontSize: 12, color: 'var(--text-subdued)' }}>
              {c.company_name && c.contact_person ? c.company_name : (c.email || c.phone || '—')}
              {' · '}owner {relationship.owner_name || '—'}
            </div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/*
          Single drawer-content scroll region. We tried a flex-split
          (pinned controls + scrolling feed) on 2026-05-07 to keep the
          HarryBox visible while the long feed scrolled — but the nested
          flex math broke scroll entirely on the live drawer. Reverted.
          The dedupe in dedupeFeed() already trims thread-noise, so a
          single overflow-y is the simpler safe default.
        */}
        <div className="drawer-content">
          {/* HarryBox first — the spec puts it at the top of the drawer. */}
          <HarryBox
            scopeKind="relationship"
            scopeId={relationship.id}
            contextLabel={scopeLabel}
            session={session}
          />

          {/* Last Updated + +Update button — single click stamps now() on
              last_contact_at. Auto-bumped by the trg_bump_last_contact_at
              trigger on every relationship_updates insert too, so the
              auto-touchpoint feed below also moves this. */}
          <div style={{
            background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10,
            padding: '12px 14px', marginBottom: 12,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Last Updated</div>
                <div style={{ fontSize: 13, color: 'var(--text-subdued)' }}>
                  {relationship.last_contact_at ? formatWhen(relationship.last_contact_at) : 'never'}
                </div>
              </div>
              {(isOwner || isAdmin) ? (
                !composeOpen ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={openCompose}
                    title="Add a note about this relationship"
                  >
                    +Update
                  </button>
                ) : null
              ) : (
                <span style={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                  {relationship.owner_name || 'owner'} only
                </span>
              )}
            </div>
            {composeOpen && (
              <div style={{ marginTop: 10 }}>
                <textarea
                  autoFocus
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveCompose();
                    else if (e.key === 'Escape') cancelCompose();
                  }}
                  placeholder="What happened? (Cmd+Enter to save, Esc to cancel)"
                  rows={3}
                  disabled={bumping}
                  style={{
                    width: '100%', boxSizing: 'border-box', resize: 'vertical',
                    fontFamily: 'inherit', fontSize: 13, lineHeight: 1.45,
                    padding: '8px 10px', borderRadius: 8,
                    border: '1px solid var(--border-subdued)',
                  }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={cancelCompose} disabled={bumping}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={saveCompose} disabled={bumping}>
                    {bumping ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Feed */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-emphasized)', marginBottom: 8, marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-subdued)' }}>
            Feed · {dedupedUpdates.length}{dedupedUpdates.length !== updates.length ? ` (${updates.length - dedupedUpdates.length} duplicates collapsed)` : ''}
          </div>

          {loadingUpdates ? (
            <div style={{ fontSize: 12, color: 'var(--text-disabled)' }}>Loading…</div>
          ) : updates.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-disabled)' }}>
              No updates yet. Auto-populates when an email, Slack message, or transcript mentions this contact.
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dedupedUpdates.map((u) => {
                const chip = chipFor(u);
                // Mig 126 RLS: owner-or-admin can edit/delete, regardless
                // of who originally authored the row. Frontend predicate
                // mirrors RLS to avoid 403s on a doomed click.
                const canEdit = isOwner || isAdmin;
                const editing = editingId === u.id;
                return (
                  <li key={u.id} style={{
                    background: '#fff', border: '1px solid #E8E6E0', borderRadius: 8,
                    padding: '10px 12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-subdued)' }}>
                        <span style={{
                          padding: '1px 7px', borderRadius: 999,
                          background: chip.bg, color: chip.fg, fontSize: 10, fontWeight: 600,
                        }}>{chip.label}</span>
                        {u._dupes > 0 ? (
                          <span style={{
                            padding: '1px 7px', borderRadius: 999,
                            background: '#F4F3EF', color: '#6E7278',
                            fontSize: 10, fontWeight: 600,
                          }} title={`${u._dupes} more replies in this thread on the same day`}>
                            +{u._dupes}
                          </span>
                        ) : null}
                        {u.source === 'manual' ? <span>by {u.author_name || '—'}</span> : null}
                        <span>{formatWhen(u.created_at)}</span>
                        {u.edited_at ? <span style={{ color: 'var(--text-disabled)' }}>(edited)</span> : null}
                      </div>
                      {canEdit && !editing ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" onClick={() => startEdit(u)} style={editBtnStyle}>edit</button>
                          <button type="button" onClick={() => deleteUpdate(u)} style={editBtnStyle}>del</button>
                        </div>
                      ) : null}
                    </div>
                    {editing ? (
                      <div>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={3}
                          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: 13, padding: '6px 8px', border: '1px solid var(--border-subdued)', borderRadius: 6 }}
                        />
                        <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                          <button type="button" className="btn btn-primary btn-sm" disabled={!editText.trim()} onClick={saveEdit}>Save</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {u.source === 'auto' && u.presence_summary ? (
                          <div style={{ fontSize: 11, color: 'var(--text-subdued)', fontStyle: 'italic', marginBottom: 4 }}>
                            {firstName} {u.presence_summary.charAt(0).toLowerCase() + u.presence_summary.slice(1)}
                          </div>
                        ) : null}
                        <div style={{ fontSize: 13, color: 'var(--text-emphasized)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                          {cleanBody(u.body, u.source)}
                        </div>
                        {Array.isArray(u.team_participants) && u.team_participants.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                            {u.team_participants.map((name, i) => (
                              <span key={`${u.id}-tp-${i}`} style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '2px 7px', borderRadius: 999,
                                fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
                                background: '#EEF2FF', color: '#3730A3',
                                border: '1px solid #C7D2FE',
                              }}
                              title={`${name.toLowerCase()} on the same ${u.signal_ref?.source_type || 'thread'}`}>
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {error ? (
            <div style={{ marginTop: 16, padding: '10px 12px', background: '#FBEAE5', color: '#A03422', borderRadius: 8, fontSize: 12 }}>
              {error}
            </div>
          ) : null}
        </div>

        <div className="drawer-footer">
          <button className="btn btn-secondary btn-sm" onClick={archive} disabled={!isOwner && !isAdmin}>
            Archive
          </button>
          <button className="btn btn-primary btn-full" onClick={onClose}>Close</button>
        </div>
      </aside>
    </>
  );
}

const editBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-subdued)',
  cursor: 'pointer',
  fontSize: 11,
  textDecoration: 'underline',
  padding: 0,
};
